import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { ActionsSetupArgs, Automation, BasicSetupArgs, TriggersSetupArgs } from './builder.types';
import { ActivatedRoute, Router } from '@angular/router';
import { LayoutService } from 'app/layout/layout.service';
import { Actions, Store, ofActionCompleted } from '@ngxs/store';
import { AutomationState } from '../state/state';
import { Automation as AutomationActions } from '../state/actions';
import { noop, tap } from 'rxjs';
import { slideInOutWidth } from 'app/shared/functions/animation.functions';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { HttpErrorResponse } from '@angular/common/http';

enum PropertiesPanel {
  Basic = 1,
  Triggers,
  Action,
}

type UI = {
  activePropertiesPanel: PropertiesPanel;
  activePropertiesId: number;
  isValid: boolean;
};

@Component({
  selector: 'app-automation-builder',
  templateUrl: './builder.component.html',
  styleUrls: ['./builder.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [slideInOutWidth],
})
export class BuilderComponent extends OnDestroyMixin implements OnInit, OnDestroy {
  automation: Automation = null;
  formData = {};
  ui: UI = { activePropertiesPanel: null, activePropertiesId: null, isValid: false };
  PropertiesPanel = PropertiesPanel;

  constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly layoutService: LayoutService,
    private readonly actions$: Actions,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly snackBar: MatSnackBar,
  ) {
    super();
  }

  ngOnInit(): void {
    setTimeout(() => this.layoutService.emitCommon('showActivitiesPanel', false), 0);

    this.formData = this.activatedRoute.snapshot.data;

    if (this.activatedRoute.snapshot.params.id) {
      if (!(this.automation = this.store.selectSnapshot(AutomationState.getSelectedAutomation))) {
        this.store.dispatch(new AutomationActions.GetById(+this.activatedRoute.snapshot.params.id));
      } else {
        this.ui = { ...this.ui, isValid: this.isAutomationValid() };
      }
    } else {
      this.automation = {
        name: 'My New Automation',
        is_active: false,
        target_model: null,
        triggers: null,
        actions: null,
      };
      this.ui = { ...this.ui, activePropertiesPanel: PropertiesPanel.Basic, activePropertiesId: null };
    }

    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionCompleted(AutomationActions.GetById),
        tap(() => {
          this.automation = this.store.selectSnapshot(AutomationState.getSelectedAutomation);
          this.ui = { ...this.ui, isValid: this.isAutomationValid() };
          this.changeDetectorRef.detectChanges();
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionCompleted(AutomationActions.Create),
        tap(({ result }) => {
          if (result.error) {
            const [error] = (result.error as HttpErrorResponse).error.automation.errors;
            this.snackBar.open(error || 'Couldn not create.', '', { duration: 3000, panelClass: 'error-snackbar' });
            return;
          }

          this.snackBar.open('Automation has been created.', '', { duration: 3000 });
          this.automation = this.store.selectSnapshot(AutomationState.getSelectedAutomation);
          this.router.navigate([this.automation.id], { relativeTo: this.activatedRoute });
        }),
      )
      .subscribe(noop);

    this.actions$
      .pipe(
        untilComponentDestroyed(this),
        ofActionCompleted(AutomationActions.Update),
        tap(() => this.snackBar.open('Automation has been updated.', '', { duration: 3000 })),
      )
      .subscribe(noop);
  }

  ngOnDestroy(): void {
    this.layoutService.emitCommon('showActivitiesPanel', true);
    super.ngOnDestroy();
  }

  cancel(): void {
    this.closeAllAndCheckValidity();
  }

  onOpenBasicSetup(): void {
    this.ui = { ...this.ui, activePropertiesPanel: PropertiesPanel.Basic, activePropertiesId: null };
  }

  onOpenTriggersSetup(): void {
    this.ui = {
      ...this.ui,
      activePropertiesPanel: PropertiesPanel.Triggers,
      activePropertiesId: this.automation.triggers?.length ? this.automation.triggers[0].id : null,
    };
  }

  onOpenActionSetup(index: number): void {
    this.ui = {
      ...this.ui,
      activePropertiesPanel: PropertiesPanel.Action,
      activePropertiesId: this.automation.actions[index].id,
    };
  }

  onBasicSetupChanged(basicArgs: BasicSetupArgs): void {
    if (this.automation.target_model && basicArgs.target_model !== this.automation.target_model) {
      this.automation = {
        ...this.automation,
        ...basicArgs,
        triggers: [],
        actions: [],
      };
    }

    const hasAnyTriggers = !!this.automation.triggers?.length;

    this.automation = {
      ...this.automation,
      ...basicArgs,
      triggers: hasAnyTriggers ? this.automation.triggers : [{ id: -1, attr: null, type: null, value: null }],
    };

    if (hasAnyTriggers) {
      this.closeAllAndCheckValidity();
    } else {
      this.ui = {
        ...this.ui,
        activePropertiesPanel: PropertiesPanel.Triggers,
        activePropertiesId: -1,
        isValid: this.isAutomationValid(),
      };
    }
  }

  onTriggersSetupChanged(triggersArgs: TriggersSetupArgs): void {
    const hasAnyActions = !!this.automation.actions?.length;

    this.automation = {
      ...this.automation,
      ...triggersArgs,
      actions: hasAnyActions ? this.automation.actions : [{ id: -1, attr: null, type: null, value: null }],
    };

    if (hasAnyActions) {
      this.closeAllAndCheckValidity();
    } else {
      this.ui = {
        ...this.ui,
        activePropertiesPanel: PropertiesPanel.Action,
        activePropertiesId: -1,
        isValid: this.isAutomationValid(),
      };
    }
  }

  onActionSetupChanged(actionArgs: ActionsSetupArgs): void {
    const index = this.automation.actions.findIndex(({ id }) => id === actionArgs.id);
    this.automation.actions[index] = actionArgs;
    this.closeAllAndCheckValidity();
  }

  onAddAction(): void {
    this.automation = {
      ...this.automation,
      actions: [...this.automation.actions, { id: -1, attr: null, type: null, value: null }],
    };
  }

  save(): void {
    if (this.isAutomationValid()) {
      this.store.dispatch(
        this.automation.id
          ? new AutomationActions.Update(this.automation)
          : new AutomationActions.Create(this.automation),
      );
    }
  }

  private isAutomationValid(): boolean {
    if (
      !this.automation.name ||
      !this.automation.target_model ||
      !this.isAutomationActionsValid() ||
      !this.isAutomationTriggersValid()
    ) {
      return false;
    }

    return true;
  }

  private isAutomationActionsValid(): boolean {
    if (
      !this.automation.actions?.length ||
      this.automation.actions.every(({ attr, type, value }) => !attr || !type || !value)
    ) {
      return false;
    }

    return true;
  }

  private isAutomationTriggersValid(): boolean {
    if (
      !this.automation.triggers?.length ||
      this.automation.triggers.every(({ attr, type, value }) => !attr || !type || !value)
    ) {
      return false;
    }

    return true;
  }

  private closeAllAndCheckValidity(): void {
    this.ui = { ...this.ui, activePropertiesPanel: null, activePropertiesId: null, isValid: this.isAutomationValid() };
  }
}
